/**
 * =============================================================================
 * ROASTBREW - APPLICATION LAYOUT WRAPPER
 * =============================================================================
 * 
 * Layout component that provides the shared structure for all authenticated pages.
 * Includes the sidebar, toggle button, and main content area.
 * 
 * This component handles:
 * - Responsive behavior (desktop/tablet/mobile breakpoints)
 * - Sidebar open/close state
 * - Ambient background effects
 * - Layout structure with proper spacing
 * 
 * USAGE:
 * Wrap page content components with AppLayout in App.jsx:
 * 
 * <AppLayout {...layoutProps}>
 *   <HomePage {...pageProps} />
 * </AppLayout>
 * 
 * PROPS:
 * @param {React.ReactNode} children - Page content to render in main area
 * @param {object} user - Current user { name, email }
 * @param {string} currentTier - Subscription tier ('free', 'pro', 'pro_plus')
 * @param {object} colors - Theme colors from config
 * @param {function} onLogout - Logout callback
 * @param {boolean} showConversations - Whether to show conversation history in sidebar
 * 
 * =============================================================================
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import { useTheme } from './ThemeContext';
import { useAuth } from './AuthContext';
import { useMathLive } from './mathliveTheme';


// =============================================================================
// CUSTOM HOOK: useWindowSize
// =============================================================================

/**
 * Hook to track window dimensions for responsive behavior
 * Returns { width, height } and updates on resize
 */
function useWindowSize() {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 1200,
    height: typeof window !== 'undefined' ? window.innerHeight : 800,
  });

  useEffect(() => {
    function handleResize() {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }

    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
}


// =============================================================================
// PAYMENT FAILED PANEL
// Shown INSTEAD of the page while the subscription's payment is failing (past_due), so nothing in the app can be
// used until the card is fixed. The sidebar stays usable, and /subscription and /profile (not inside AppLayout)
// are never covered, so the customer can always fix their card.
// =============================================================================

function PaymentFailedPanel({ colors, isLight }) {
  const navigate = useNavigate();
  const button = (primary) => ({
    padding: '14px 22px',
    fontSize: '14px',
    fontWeight: 500,
    fontFamily: '"DM Sans", sans-serif',
    borderRadius: '8px',
    cursor: 'pointer',
    border: primary ? 'none' : `1px solid ${isLight ? 'rgba(0,0,0,0.12)' : 'rgba(255,255,255,0.12)'}`,
    background: primary ? '#E24B4A' : 'transparent',
    color: primary ? '#FFFFFF' : colors.text,
  });
  return (
    <div
      role="alert"
      style={{
        minHeight: '100dvh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '96px 24px 48px',
        boxSizing: 'border-box',
      }}
    >
      <div style={{ maxWidth: '440px', width: '100%', textAlign: 'center' }}>
        <div style={{
          width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(226, 75, 74, 0.1)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px',
        }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#E24B4A" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4M12 16h.01" />
          </svg>
        </div>
        <h2 style={{ fontSize: '28px', fontWeight: 400, margin: '0 0 12px', color: colors.text, fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
          Payment failed
        </h2>
        <p style={{ fontSize: '15px', lineHeight: 1.6, margin: '0 0 32px', color: colors.muted, fontFamily: '"DM Sans", sans-serif' }}>
          Your last payment didn't go through. Update your card to keep using RoastBrew.
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button type="button" style={button(false)} onClick={() => navigate('/subscription')}>Manage subscription</button>
          <button type="button" style={button(true)} onClick={() => navigate('/profile')}>Update card</button>
        </div>
      </div>
    </div>
  );
}


// =============================================================================
// APP LAYOUT COMPONENT
// =============================================================================

export default function AppLayout({
  children,
  onLogout = () => {},
  showConversations = true,
}) {
  // ---------------------------------------------------------------------------
  // RESPONSIVE STATE
  // ---------------------------------------------------------------------------
  
  const { width } = useWindowSize();
  const { profile } = useAuth();
  const paymentFailed = profile?.subscription?.status === 'past_due';
  // Breakpoints must key off the BROWSER VIEWPORT (innerWidth), not window.screen —
  // window.screen reports the physical monitor size, so on a desktop Mac Safari the
  // short side (e.g. 900px on a 1440x900 laptop) fell into the tablet range and forced
  // the iPad layout. Viewport width is the correct signal for responsive layout.
  const isMobile = width < 768;
  const isTablet = width >= 768 && width < 1024;
  const isDesktop = width >= 1024;

  // ---------------------------------------------------------------------------
  // SIDEBAR STATE
  // ---------------------------------------------------------------------------
  
  /**
   * sidebarOpen: Controls sidebar visibility
   * - Desktop: Default open, persists user preference
   * - Tablet/Mobile: Default closed, opens as overlay
   */
  const [sidebarOpen, setSidebarOpen] = useState(isDesktop);

  // Auto-adjust sidebar based on viewport changes
  useEffect(() => {
    if (isMobile || isTablet) {
      setSidebarOpen(false);
    } else {
      setSidebarOpen(true);
    }
  }, [isMobile, isTablet]);

  // ---------------------------------------------------------------------------
  // COMPUTED VALUES
  // ---------------------------------------------------------------------------
  
  const { colors, isLight: isLightTheme } = useTheme();

  // ---------------------------------------------------------------------------
  // MATHLIVE
  // ---------------------------------------------------------------------------

  // Sets the CSS variables an editable mathfield needs — the caret, the
  // selection, and the highlight MathLive paints behind the group containing
  // the caret.
  //
  // THE VIRTUAL KEYBOARD IS NOT CONFIGURED ANYWHERE. All of it was removed and
  // will be rebuilt from scratch. MathLive's own default applies until then.
  useMathLive();


  // ---------------------------------------------------------------------------
  // EVENT HANDLERS
  // ---------------------------------------------------------------------------
  
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const closeSidebar = () => {
    if (isMobile || isTablet) {
      setSidebarOpen(false);
    }
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  
  return (
    <div
      style={{
        // dvh tracks the live visible height; 100vh is the height with the iOS
        // browser chrome collapsed, so it overstates the viewport while the URL
        // bar is showing.
        //
        // No vh fallback here: the duplicate-declaration trick .rb-sidebar uses
        // only works in a real stylesheet. In a JS style object the second key
        // just overwrites the first, so a fallback pair would silently drop the
        // vh value and trip no-dupe-keys. dvh has shipped since Safari 15.4.
        minHeight: '100dvh',
        width: '100%',
        background: colors.bg,
        fontFamily: '"Cormorant Garamond", Georgia, serif',
        display: 'flex',
        position: 'relative',
        overflowX: 'hidden',
        transition: 'background 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      {/* =================================================================
          AMBIENT BACKGROUND
          Subtle gradient overlay for visual depth
          Varies based on tier for differentiation
          ================================================================= */}
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: isLightTheme
            ? 'radial-gradient(ellipse at 50% 30%, rgba(201, 169, 98, 0.04) 0%, transparent 60%)'
            : `radial-gradient(ellipse at 0% 0%, ${colors.accent}08 0%, transparent 50%), radial-gradient(ellipse at 100% 100%, ${colors.accent}05 0%, transparent 50%)`,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* =================================================================
          SIDEBAR
          ================================================================= */}
      <Sidebar
        isOpen={sidebarOpen}
        onToggle={toggleSidebar}
        onClose={closeSidebar}
        onLogout={onLogout}
        isMobile={isMobile}
        isTablet={isTablet}
        isDesktop={isDesktop}
        showConversations={showConversations}
      />

      {/* =================================================================
          SIDEBAR TOGGLE BUTTON
          Positioned differently based on viewport and sidebar state
          ================================================================= */}
      <button
        onClick={toggleSidebar}
        style={{
          // The only position:fixed interactive element in the app, which is
          // why it is the only control that can end up unreachable when the
          // visual and layout viewports drift apart on iOS. Pull-to-refresh is
          // declined in App.jsx for that reason; the safe-area inset below
          // keeps it clear of the status bar and notch, which is the other way
          // it can land somewhere untappable.
          position: 'fixed',
          top: isMobile
            ? 'calc(16px + env(safe-area-inset-top, 0px))'
            : '28px',
          left: (isMobile || isTablet)
            ? '16px'
            : (sidebarOpen ? '292px' : '20px'),
          width: isMobile ? '44px' : '40px',
          height: isMobile ? '44px' : '40px',
          borderRadius: '10px',
          background: colors.sidebar,
          border: `1px solid ${isLightTheme ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.08)'}`,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: colors.muted,
          zIndex: (isMobile || isTablet) ? 997 : 101,
          transition: 'left 0.4s cubic-bezier(0.4, 0, 0.2, 1), color 0.2s ease',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
        }}
        onMouseEnter={(e) => e.currentTarget.style.color = colors.text}
        onMouseLeave={(e) => e.currentTarget.style.color = colors.muted}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          {(isMobile || isTablet) ? (
            // Hamburger menu for mobile/tablet
            <path d="M3 12h18M3 6h18M3 18h18" strokeLinecap="round" />
          ) : (
            // Chevron for desktop
            sidebarOpen 
              ? <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
              : <path d="M9 18l6-6-6-6" strokeLinecap="round" strokeLinejoin="round" />
          )}
        </svg>
      </button>

      {/* =================================================================
          MAIN CONTENT AREA
          Adjusts margin based on sidebar state (desktop only)
          ================================================================= */}
      <main
        style={{
          flex: 1,
          marginLeft: isDesktop && sidebarOpen ? '280px' : '0',
          minHeight: '100dvh',
          width: isDesktop && sidebarOpen ? 'calc(100% - 280px)' : '100%',
          maxWidth: '100%',
          // Hidden on BOTH axes, not just x. Hiding only x silently turns <main> into a
          // vertical scroll box, so any entrance animation that briefly pokes below it
          // (the fadeInUp rise on Search, Library, Profile…) gave <main> its own
          // scrollbar for a moment — the content squeezed narrower, then snapped back.
          // With y hidden too, <main> can never show a scrollbar of its own. (It stays
          // the same kind of box as before, so nothing inside it behaves differently.)
          overflow: 'hidden',
          transition: 'margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1), width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        {/* Payment failing: the panel replaces the page (nothing behind it is mounted). */}
        {paymentFailed && <PaymentFailedPanel colors={colors} isLight={isLightTheme} />}

        {/* Clone children and pass responsive props */}
        {!paymentFailed && React.Children.map(children, child => {
          if (React.isValidElement(child)) {
            return React.cloneElement(child, {
              isMobile,
              isTablet,
              isDesktop,
              sidebarOpen,
            });
          }
          return child;
        })}

      </main>
    </div>
  );
}
